import React, { useState } from "react";
import API_BASE from "../config/api";
import { resolveImageUrl } from "../utils/siteHelpers";

/*
  Direct file-upload field for business website images (CR B2, B3, B4).
  Replaces the old "paste an image URL" only workflow — the owner picks a
  file, it uploads immediately to /api/upload_business_file.php and the
  returned (relative) path is handed back through onChange, exactly the
  same shape the old ImageUrlField used to hand back a pasted URL. This
  keeps save_business_site.php / the DB schema completely unchanged.

  props:
    value        - current stored path/URL (string)
    onChange     - (newValue: string) => void
    purpose      - "logo" | "cover" | "gallery" | "product"  (required)
    personaluid  - used only to namespace the uploaded file's folder
    placeholder  - label shown on the drop zone
    allowUrl     - if true (default), also show a collapsible "paste a link instead" option
*/
export default function ImageUploadField({
    value,
    onChange,
    purpose,
    personaluid,
    placeholder = "Click to upload an image",
    allowUrl = true,
    authHeaders = null, // optional, e.g. { "X-Admin-Token": token } for admin-gated purposes
}) {
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState("");
    const [showUrlBox, setShowUrlBox] = useState(false);

    const previewSrc = resolveImageUrl(API_BASE, value);

    const handleFile = async (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = ""; // allow re-selecting the same file later
        if (!file) return;

        setError("");
        setUploading(true);

        const fd = new FormData();
        fd.append("file", file);
        fd.append("purpose", purpose);
        if (personaluid) fd.append("personaluid", personaluid);

        try {
            const res = await fetch(`${API_BASE}/api/upload_business_file.php`, {
                method: "POST",
                headers: authHeaders || undefined,
                body: fd,
            });
            const data = await res.json();
            setUploading(false);
            if (data.success) {
                onChange(data.url);
            } else {
                setError(data.message || "Upload failed.");
            }
        } catch {
            setUploading(false);
            setError("Network error while uploading.");
        }
    };

    return (
        <div className="dn-imgupload">
            <div className="dn-imgupload-row">
                <div className="dn-imgupload-preview">
                    {value ? (
                        <img src={previewSrc} alt="" onError={(e) => (e.target.style.display = "none")} />
                    ) : (
                        <i className="bi bi-image" />
                    )}
                </div>

                <div className="flex-grow-1">
                    <label className="btn btn-sm btn-outline-primary mb-0">
                        <i className="bi bi-cloud-upload-fill me-1" />
                        {uploading ? "Uploading..." : value ? "Replace image" : placeholder}
                        <input type="file" accept="image/*" hidden disabled={uploading} onChange={handleFile} />
                    </label>
                    {value && (
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-danger ms-2"
                            onClick={() => onChange("")}
                            disabled={uploading}
                        >
                            Remove
                        </button>
                    )}
                    {allowUrl && (
                        <button
                            type="button"
                            className="btn btn-sm btn-link"
                            onClick={() => setShowUrlBox((s) => !s)}
                        >
                            {showUrlBox ? "Hide link option" : "Or paste an image link instead"}
                        </button>
                    )}
                </div>
            </div>

            {showUrlBox && (
                <input
                    type="text"
                    className="form-control form-control-sm mt-2"
                    placeholder="https://example.com/photo.jpg"
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                />
            )}

            {error && <div className="dn-imgupload-error"><i className="bi bi-exclamation-circle me-1" />{error}</div>}

            <style>{`
                .dn-imgupload-row{display:flex;gap:10px;align-items:center;}
                .dn-imgupload-preview{width:56px;height:56px;border-radius:8px;border:1px solid #dee2e6;flex-shrink:0;display:flex;align-items:center;justify-content:center;overflow:hidden;background:#f8f9fa;color:#adb5bd;font-size:20px;}
                .dn-imgupload-preview img{width:100%;height:100%;object-fit:cover;}
                .dn-imgupload-error{font-size:11.5px;color:#c0392b;margin-top:4px;}
            `}</style>
        </div>
    );
}
